/**
 * Data Normalization Hooks
 * Provides safe, consistent data access patterns across dashboard components
 */

import { useMemo } from 'react';
import { safeNumber, safeDivide } from '../utils/format';

/**
 * Hook for normalized user analytics data access
 * @param {Object} userData - Raw user analytics data
 * @param {Object} sessionData - Raw session analytics data
 * @param {Object} crashData - Raw crash analytics data
 * @returns {Object} - Normalized data object with safe accessors
 */
export const useNormalizedUserData = (userData, sessionData, crashData) => {
  return useMemo(() => {
    // Calculate base values first
    const totalUsers = safeNumber(userData?.totalUserCount || userData?.totalUsers, 0);
    const verifiedUsers = safeNumber(userData?.verifiedUsers, 0);
    const onboardedUsers = safeNumber(userData?.onboardedUsers, 0);
    const walletUsers = safeNumber(userData?.walletUsers, 0);
    const healthyWallets = safeNumber(userData?.healthyWallets, 0);
    
    return {
      // User counts
      totalUsers,
      iosUsers: safeNumber(userData?.totalUsersIOS || userData?.iosUsers, 0),
      androidUsers: safeNumber(userData?.totalUsersAndroid || userData?.androidUsers, 0),
      solanaUsers: safeNumber(userData?.solanaUsers, 0),
      periodUsers: safeNumber(userData?.periodUserCount, 0),
      
      // User status
      verifiedUsers,
      onboardedUsers,
      walletUsers,
      healthyWallets,
      
      // Session metrics
      totalSessions: safeNumber(sessionData?.totalSessions, 0),
      activeUsers: safeNumber(sessionData?.activeUsers, 0),
      recentLogins: safeNumber(sessionData?.recentLogins, 0),
      averageSessionsPerUser: safeNumber(
        sessionData?.averageSessionCountPerUser || 
        userData?.averageSessionsPerUser,
        0
      ),
      
      // Crash metrics
      totalCrashes: safeNumber(
        crashData?.totalCrashCount || 
        userData?.crashCount,
        0
      ),
      iosCrashes: safeNumber(crashData?.totalCrashCountByPlatform?.ios, 0),
      androidCrashes: safeNumber(crashData?.totalCrashCountByPlatform?.android, 0),
      
      // Computed percentages
      verifiedPercentage: safeDivide(verifiedUsers, totalUsers, 0) * 100,
      onboardedPercentage: safeDivide(onboardedUsers, totalUsers, 0) * 100,
      walletPercentage: safeDivide(walletUsers, totalUsers, 0) * 100,
      healthyWalletPercentage: safeDivide(healthyWallets, walletUsers || 1, 0) * 100
    };
  }, [userData, sessionData, crashData]);
};

/**
 * Hook for normalized split analytics data access
 * @param {Object} splitData - Raw split analytics data
 * @returns {Object} - Normalized data object with safe accessors
 */
export const useNormalizedSplitData = (splitData) => {
  return useMemo(() => {
    const normalized = {
      // Basic counts
      totalSplits: safeNumber(splitData?.totalSplits, 0),
      totalParticipants: safeNumber(splitData?.totalParticipants, 0),
      totalItems: safeNumber(splitData?.totalItems, 0),
      
      // Split status
      activeSplits: safeNumber(splitData?.activeSplits, 0),
      completedSplits: safeNumber(splitData?.completedSplits, 0),
      pendingSplits: safeNumber(splitData?.pendingSplits, 0),
      
      // Split types
      fairSplits: safeNumber(splitData?.fairSplits, 0),
      degenSplits: safeNumber(splitData?.degenSplits, 0),
      
      // Financial
      totalAmount: safeNumber(splitData?.totalAmount, 0),
      totalAmountOwed: safeNumber(splitData?.totalAmountOwed, 0),
      totalAmountPaid: safeNumber(splitData?.totalAmountPaid, 0),
      totalWalletAmount: safeNumber(splitData?.totalWalletAmount, 0),
      splitWalletsCount: safeNumber(splitData?.splitWalletsCount, 0),
      
      // Computed values
      averageSplitAmount: safeNumber(splitData?.averageSplitAmount, 0),
      averageParticipants: safeNumber(splitData?.averageParticipants, 0),
      averageItems: safeNumber(splitData?.averageItems, 0),
      
      // Computed percentages
      activePercentage: safeDivide(
        safeNumber(splitData?.activeSplits, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ) * 100,
      
      completedPercentage: safeDivide(
        safeNumber(splitData?.completedSplits, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ) * 100,
      
      pendingPercentage: safeDivide(
        safeNumber(splitData?.pendingSplits, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ) * 100,
      
      fairPercentage: safeDivide(
        safeNumber(splitData?.fairSplits, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ) * 100,
      
      degenPercentage: safeDivide(
        safeNumber(splitData?.degenSplits, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ) * 100,
      
      paymentRate: safeDivide(
        safeNumber(splitData?.totalAmountPaid, 0),
        safeNumber(splitData?.totalAmountOwed, 1),
        0
      ) * 100,
      
      splitStatusRatio: safeDivide(
        safeNumber(splitData?.completedSplits, 0),
        safeNumber(splitData?.activeSplits, 1),
        0
      ),
      
      averageInvitesPerSplit: safeDivide(
        safeNumber(splitData?.totalParticipants, 0),
        safeNumber(splitData?.totalSplits, 1),
        0
      ),
      
      otherSplits: Math.max(
        0,
        safeNumber(splitData?.totalSplits, 0) - 
        safeNumber(splitData?.fairSplits, 0) - 
        safeNumber(splitData?.degenSplits, 0)
      ),
      
      // Most active users
      mostActiveUsers: splitData?.mostActiveUsers || []
    };
    
    return normalized;
  }, [splitData]);
};

/**
 * Hook for normalized transaction analytics data access
 * @param {Object} txData - Raw transaction analytics data
 * @returns {Object} - Normalized data object with safe accessors
 */
export const useNormalizedTransactionData = (txData) => {
  return useMemo(() => {
    const normalized = {
      // Request/Send counts
      totalRequestCount: safeNumber(
        txData?.totalRequestCount || txData?.totalRequests,
        0
      ),
      totalSendCount: safeNumber(
        txData?.totalSendCount || txData?.totalSends,
        0
      ),
      
      // Request/Send volumes
      totalRequestVolume: safeNumber(txData?.totalRequestVolume, 0),
      totalSendVolume: safeNumber(txData?.totalSendVolume, 0),
      
      // In-app split volume
      totalSplitVolumeInApp: safeNumber(
        txData?.totalSplitVolumeInApp,
        safeNumber(txData?.totalRequestVolume, 0) + safeNumber(txData?.totalSendVolume, 0)
      ),
      totalSharedValueInApp: safeNumber(
        txData?.totalSharedValueInApp,
        safeNumber(txData?.totalRequestVolume, 0) + safeNumber(txData?.totalSendVolume, 0)
      ),
      
      // Transaction counts
      totalTransactionCount: safeNumber(txData?.totalTransactionCount, 0),
      totalTransactionCountInApp: safeNumber(
        txData?.totalTransactionCountInApp || txData?.totalTransactionCount,
        0
      ),
      
      // Averages
      averageTicketValue: safeNumber(
        txData?.averageTicketValue || txData?.averageTransactionValue,
        0
      ),
      averageTransactionValue: safeNumber(txData?.averageTransactionValue, 0),
      averageTransactionValueInApp: safeNumber(
        txData?.averageTransactionValueInApp || txData?.averageTransactionValue,
        0
      ),
      
      // Deposits & Withdrawals
      totalInputVolume: safeNumber(
        txData?.totalInputVolume || txData?.totalDepositValue,
        0
      ),
      totalDepositValue: safeNumber(
        txData?.totalDepositValue || txData?.totalInputVolume,
        0
      ),
      totalDepositVolume: safeNumber(txData?.totalDepositVolume, 0),
      totalWithdrawalVolume: safeNumber(
        txData?.totalWithdrawalVolume || txData?.totalWithdrawalValue,
        0
      ),
      totalWithdrawalValue: safeNumber(
        txData?.totalWithdrawalValue || txData?.totalWithdrawalVolume,
        0
      ),
      
      // Other metrics
      lockedFundsValue: safeNumber(txData?.lockedFundsValue, 0),
      totalGasFees: safeNumber(txData?.totalGasFees, 0),
      totalCompanyFees: safeNumber(txData?.totalCompanyFees, 0),
      gasFeeCoveredByCompany: safeNumber(txData?.gasFeeCoveredByCompany, 0),
      
      // MoonPay & Payment Requests
      moonpayVolume: safeNumber(txData?.moonpayVolume, 0),
      paymentRequestsCount: safeNumber(txData?.paymentRequestsCount, 0),
      withdrawalRequestsCount: safeNumber(txData?.withdrawalRequestsCount, 0),
      
      // Split types
      fairSplitsValue: safeNumber(
        txData?.splitTypeValueDistribution?.fair || txData?.fairSplits,
        0
      ),
      degenSplitsValue: safeNumber(
        txData?.splitTypeValueDistribution?.degen || txData?.degenSplits,
        0
      ),
      
      // Computed values
      netVolume: safeNumber(txData?.totalInputVolume || txData?.totalDepositValue, 0) - 
                 safeNumber(txData?.totalWithdrawalVolume || txData?.totalWithdrawalValue, 0),
      
      totalRequestSendVolume: safeNumber(txData?.totalRequestVolume, 0) + 
                               safeNumber(txData?.totalSendVolume, 0),
      
      // Transaction value (if available)
      totalTransactionValue: safeNumber(txData?.totalTransactionValue, 0),
      
      // Blockchain networks
      blockchainNetworks: txData?.blockchainNetworks || {}
    };
    
    return normalized;
  }, [txData]);
};

